import React, { memo } from "react";
import { Box, Card, CardBody,Button } from "@chakra-ui/react";
import { ConfirmSegmentCouponUseDialog } from "./ConfirmSegmentCouponUseDialog";
import { ValidSegmentCoupon } from "../../../hooks/membership/useFetchValidCoupon";
import { CouponLayout } from "../../molecules/CouponLayout";
import { useRecoilValue } from "recoil";
import { lineProfileState } from "../../../store/lineProfileState";
import { customerRestaurantId } from "../../../store/customerRestaurantId";
import { useFetchSoCouponUsageStatusByLineId } from "../../../hooks/socoupon/useFetchSoCouponUsageStatusByLineId";

export const CustomerValidSegmentCoupon: React.FC<ValidSegmentCoupon> = memo(({id, img, title, text, expiration_date, notes}) => {
  const lineProfile = useRecoilValue(lineProfileState);
  const restaurantId = useRecoilValue(customerRestaurantId);
  const lineId = lineProfile!.userId;
  const { soCouponUsageStatus, isLoading } = useFetchSoCouponUsageStatusByLineId(restaurantId, lineId);

  // デバッグ有効判定（ENV=false なら常に無効）
  const isDebugEnabledStrict = (): boolean => {
    if (process.env.REACT_APP_MANLABO_DEBUG !== 'true') return false; // ENVがfalseなら無効
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w: any = window as any;
      if (w && w.__MANLABO_DEBUG__ === false) return false;
    } catch {}
    try {
      if (localStorage.getItem('MANLABO_DEBUG') === '0') return false;
    } catch {}
    return true; // ENV=true かつ明示OFFでない
  };
  const debug = isDebugEnabledStrict();

  // 循環参照に強い stringify（alert 用）
  const safeStringify = (value: any) => {
    const cache = new Set<any>();
    const s = JSON.stringify(
      value,
      (_k, v) => {
        if (typeof v === 'object' && v !== null) {
          if (cache.has(v)) return '[Circular]';
          cache.add(v);
        }
        if (v instanceof Error) return { name: v.name, message: v.message, stack: v.stack };
        return v;
      },
      2
    );
    cache.clear();
    return s;
  };

  // クーポンID単位の厳密判定
  const rawStatus: any = soCouponUsageStatus;
  const isMap = typeof rawStatus === 'object' && rawStatus !== null;
  const hasCouponKey = isMap && Object.prototype.hasOwnProperty.call(rawStatus, id);
  const baseUsed =
    hasCouponKey
      ? (rawStatus[id] === 'used' || rawStatus[id] === true)
      : (typeof rawStatus === 'string' ? false : false);

  // デバッグモード: none/freeze/forceTrue/forceFalse
  type DebugMode = 'none' | 'freeze' | 'forceTrue' | 'forceFalse';
  const [debugMode, setDebugMode] = React.useState<DebugMode>(() => {
    try { return (localStorage.getItem('parent.debug.mode') as DebugMode) || 'none'; } catch { return 'none'; }
  });
  React.useEffect(() => {
    try { localStorage.setItem('parent.debug.mode', debugMode); } catch {}
  }, [debugMode]);

  // 楽観更新
  const [localUsed, setLocalUsed] = React.useState<boolean>(baseUsed);
  React.useEffect(() => {
    if (baseUsed) setLocalUsed(true);
  }, [baseUsed]);

  // 初期値固定
  const initialUsedRef = React.useRef<boolean>(baseUsed);

  // Loading時の未使用扱い
  const [treatLoadingAsUnused, setTreatLoadingAsUnused] = React.useState<boolean>(() => {
    try { return localStorage.getItem('parent.debug.loadingAsUnused') === '1'; } catch { return true; }
  });
  React.useEffect(() => {
    try { localStorage.setItem('parent.debug.loadingAsUnused', treatLoadingAsUnused ? '1' : '0'); } catch {}
  }, [treatLoadingAsUnused]);

  const effectiveBase = localUsed || baseUsed;
  const isUsedEffective =
    debugMode === 'forceTrue' ? true :
    debugMode === 'forceFalse' ? false :
    debugMode === 'freeze' ? initialUsedRef.current :
    (treatLoadingAsUnused && isLoading) ? (localUsed ? true : false) :
    effectiveBase;

  return (
    <Box>
      <Box p={5}>
        <Card rounded='2xl' boxShadow='rgba(0, 0, 0, 0.1) 0px 0px 6px'>
          <CardBody >
            <CouponLayout
              title={title}
              explanation={text}
              expiration_date={expiration_date}
              notes={notes}
              img={img}
            />
          </CardBody>
        </Card>
      </Box>
      <ConfirmSegmentCouponUseDialog
        couponId={id}
        isUsed={isUsedEffective}
        // 押下直後: 即「使用済み」に
        onUsed={() => setLocalUsed(true)}
        // エラー時: 元に戻す
        onUsedRevert={() => setLocalUsed(false)}
      />
      {debug && (
        <Box mt={2} px={5} fontSize='12px' color='gray.600'>
          <Box mb={1}>
            [Debug-Parent]
            statusRaw={typeof soCouponUsageStatus === 'object' ? '(object)' : String(soCouponUsageStatus)}
            baseUsed={String(baseUsed)}
            init={String(initialUsedRef.current)}
            isLoading={String(isLoading)}
            pass={String(isUsedEffective)}
            isMap={String(isMap)}
            hasCouponKey={String(hasCouponKey)}
          </Box>
          <Box display="flex" gap="6px" flexWrap="wrap">
            <Button size="xs" variant={debugMode === 'none' ? 'solid' : 'outline'} onClick={() => setDebugMode('none')}>Mode:None</Button>
            <Button size="xs" variant={debugMode === 'freeze' ? 'solid' : 'outline'} onClick={() => setDebugMode('freeze')}>Freeze初期値</Button>
            <Button size="xs" variant={debugMode === 'forceFalse' ? 'solid' : 'outline'} onClick={() => setDebugMode('forceFalse')}>Force未使用</Button>
            <Button size="xs" variant={debugMode === 'forceTrue' ? 'solid' : 'outline'} onClick={() => setDebugMode('forceTrue')}>Force使用済</Button>
            <Button size="xs" variant={treatLoadingAsUnused ? 'solid' : 'outline'} onClick={() => setTreatLoadingAsUnused(v => !v)}>Loadingは未使用扱い</Button>
            <Button size="xs" variant="outline" onClick={() => { setDebugMode('none'); setTreatLoadingAsUnused(true); }}>Reset</Button>
          </Box>
        </Box>
      )}
    </Box>
  );
});